// ABI helpers for raw eth_call answers (a word is 32 bytes, 64 hex characters). `body` is the hex
// answer without its 0x prefix.

export const word = (body: string, i: number): string => body.slice(i * 64, i * 64 + 64);
export const wordBig = (body: string, i: number): bigint => BigInt('0x' + (word(body, i) || '0'));
export const wordAddr = (body: string, i: number): string => '0x' + word(body, i).slice(24);
export const encAddr = (a: string): string => a.slice(2).toLowerCase().padStart(64, '0');
export const encUint = (n: number | bigint): string => BigInt(n).toString(16).padStart(64, '0');
export const hexToBig = (h: string): bigint => BigInt(h);
export const stripHex = (h: string): string => (h.startsWith('0x') ? h.slice(2) : h);

// C0 controls and DEL, built from escapes so the source holds no control characters itself.
// eslint-disable-next-line no-control-regex
const CONTROL = new RegExp('[\\x00-\\x1f\\x7f]', 'g');

/** UTF-8 decode (issuer names contain a bullet), then drop control characters. */
export function bytesToText(hexBody: string): string {
  const bytes = new Uint8Array(hexBody.length / 2);
  for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hexBody.slice(i * 2, i * 2 + 2), 16) || 0;
  return new TextDecoder('utf-8').decode(bytes).replace(CONTROL, '').trim();
}

/** Dynamic string: the word at offsetWord holds a byte offset to (length, bytes). */
export function abiString(body: string, offsetWord: number): string {
  const off = Number(wordBig(body, offsetWord)) * 2;
  const len = Number(BigInt('0x' + (body.slice(off, off + 64) || '0')));
  return bytesToText(body.slice(off + 64, off + 64 + len * 2));
}

export interface Describe {
  official: boolean;
  symbol: string;
  name: string;
  uid: string;
  uiMultiplier: bigint;
  tokenPaused: boolean;
  oraclePaused: boolean;
}

export function decodeDescribe(hex: string): Describe {
  const b = stripHex(hex || '0x');
  if (b.length < 7 * 64) throw new Error('short describe() return');
  return {
    official: wordBig(b, 0) === 1n,
    symbol: abiString(b, 1),
    name: abiString(b, 2),
    uid: '0x' + word(b, 3),
    uiMultiplier: wordBig(b, 4),
    tokenPaused: wordBig(b, 5) === 1n,
    oraclePaused: wordBig(b, 6) === 1n,
  };
}

export interface PoolKeyLike {
  currency0: string;
  currency1: string;
  fee: number;
  tickSpacing: number;
  hooks: string;
}

export const poolKeyData = (k: PoolKeyLike): string =>
  encAddr(k.currency0) + encAddr(k.currency1) + encUint(k.fee) + encUint(k.tickSpacing) + encAddr(k.hooks);
